import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { collection, query, where, orderBy, getDocs, type Timestamp } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { db } from '../lib/firestore'
import { functions } from '../lib/functions'
import { logGaEvent } from '../lib/analytics'
import { Layout } from '../components/layout/Layout'
import { ManagedSeo } from '../components/seo/ManagedSeo'
import { BringScottModal } from '../components/schedule/BringScottModal'
import { MasterclassTicketModal } from '../components/masterclass/MasterclassTicketModal'

// ─── TYPES ──────────────────────────────────────────────────────────────────

type EventType = 'masterclass' | 'lecture' | 'expo'
type FilterType = 'all' | EventType

interface ScheduleEvent {
  id: string
  type: EventType
  title: string
  date: Date
  endDate: Date | null
  location: string
  venueName: string
  venueAddress: string
  description: string
  boothNumber: string
  speakingTopics: string
  riderSoldOut: boolean
  /** Rider spots left. Masterclasses only; undefined for lectures and expos. */
  riderSeatsRemaining?: number
  durationDays?: 2 | 3
}

interface CheckoutResult {
  url: string | null
}

const createLectureCheckout = httpsCallable<{ eventId: string }, CheckoutResult>(
  functions,
  'createLectureCheckout',
)

// ─── HELPERS ────────────────────────────────────────────────────────────────

function formatDateRange(start: Date, end: Date | null): string {
  const opts: Intl.DateTimeFormatOptions = { weekday: 'short', month: 'short', day: 'numeric' }
  const yearOpts: Intl.DateTimeFormatOptions = { ...opts, year: 'numeric' }
  if (!end) {
    return start.toLocaleDateString('en-US', yearOpts)
  }
  const s = start.toLocaleDateString('en-US', opts)
  const e = end.toLocaleDateString('en-US', yearOpts)
  return `${s} – ${e}`
}

// Uses local-time setDate/getDate to match formatDateRange's local-time output.
// (Same helper exists on ClinicsPage.tsx — keep them in sync.)
function getEventDayLabels(startDate: Date, durationDays: number): string[] {
  const labels: string[] = []
  for (let i = 0; i < durationDays; i++) {
    const d = new Date(startDate)
    d.setDate(startDate.getDate() + i)
    labels.push(d.toLocaleDateString('en-US', { weekday: 'long' }))
  }
  return labels
}

function generateIcs(event: ScheduleEvent): void {
  const pad = (n: number) => n.toString().padStart(2, '0')
  const toIcsDate = (d: Date) =>
    `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`

  const start = toIcsDate(event.date)
  const end = event.endDate
    ? toIcsDate(new Date(event.endDate.getTime() + 86400000))
    : toIcsDate(new Date(event.date.getTime() + 86400000))

  const description = [
    event.boothNumber ? `Booth: ${event.boothNumber}` : '',
    event.speakingTopics || event.description || '',
  ].filter(Boolean).join('\\n')

  const ics = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Advantage Horsemanship//Schedule//EN',
    'BEGIN:VEVENT',
    `DTSTART;VALUE=DATE:${start}`,
    `DTEND;VALUE=DATE:${end}`,
    `SUMMARY:Scott Purdum at ${event.title}`,
    `LOCATION:${event.location}${event.venueName ? `, ${event.venueName}` : ''}`,
    `DESCRIPTION:${description}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n')

  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${event.title.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}.ics`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

// ─── PAGE ───────────────────────────────────────────────────────────────────

export function SchedulePage() {
  const [events, setEvents] = useState<ScheduleEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<FilterType>('all')
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [modalEvent, setModalEvent] = useState<ScheduleEvent | null>(null)
  const [modalTicketType, setModalTicketType] = useState<'rider' | 'auditor'>('rider')
  const [modalDay, setModalDay] = useState<string | undefined>(undefined)

  function openTicketModal(
    event: ScheduleEvent,
    ticketType: 'rider' | 'auditor',
    day?: string,
  ) {
    setModalEvent(event)
    setModalTicketType(ticketType)
    setModalDay(day)
    // Step 2 of 4. Same event as the masterclass page, deliberately: both open
    // the same modal, so they must report into the same funnel.
    logGaEvent('select_item', {
      item_list_name: 'Schedule',
      items: [{
        item_id: `masterclass-${ticketType}-${event.id}`,
        item_name: event.title,
        item_category: ticketType,
        item_variant: day ?? `${event.durationDays ?? 3}-day`,
        quantity: 1,
      }],
    })
  }

  function closeTicketModal() {
    setModalEvent(null)
    setModalDay(undefined)
  }

  useEffect(() => {
    async function fetchAll() {
      const merged: ScheduleEvent[] = []

      // Fetch masterclass events
      try {
        const mq = query(
          collection(db, 'masterclassEvents'),
          where('active', '==', true),
          orderBy('startDate', 'asc'),
        )
        const mSnap = await getDocs(mq)
        for (const d of mSnap.docs) {
          const data = d.data()
          const startDate = (data.startDate as Timestamp).toDate()
          merged.push({
            id: `mc-${d.id}`,
            type: 'masterclass',
            title: (data.eventName as string) ?? 'Riders Foundation Masterclass',
            date: startDate,
            endDate: data.endDate ? (data.endDate as Timestamp).toDate() : null,
            location: (data.location as string) ?? '',
            venueName: (data.venueName as string) ?? '',
            venueAddress: (data.venueAddress as string) ?? '',
            description: '',
            boothNumber: '',
            speakingTopics: '',
            riderSoldOut: (data.riderSoldOut as boolean) ?? false,
            // Caps the "who else is riding" dropdown so a group is never
            // offered more spots than the arena has left. Events created before
            // capacity became editable fall back to 10.
            riderSeatsRemaining: Math.max(
              0,
              ((data.riderCapacity as number) || 10) - ((data.riderTicketsSold as number) ?? 0),
            ),
            durationDays: data.durationDays === 2 ? 2 : data.durationDays === 3 ? 3 : undefined,
          })
        }
      } catch {
        // masterclassEvents may not have composite index yet
      }

      // Fetch schedule events (lectures + expos)
      try {
        const sq = query(
          collection(db, 'scheduleEvents'),
          where('active', '==', true),
          orderBy('date', 'asc'),
        )
        const sSnap = await getDocs(sq)
        for (const d of sSnap.docs) {
          const data = d.data()
          const dateStr = (data.date as string) ?? ''
          const eventDate = new Date(dateStr)
          merged.push({
            id: d.id,
            type: (data.type as EventType) ?? 'lecture',
            title: (data.title as string) ?? '',
            date: eventDate,
            endDate: data.endDate ? new Date(data.endDate as string) : null,
            location: (data.location as string) ?? '',
            venueName: (data.venueName as string) ?? '',
            venueAddress: (data.venueAddress as string) ?? '',
            description: (data.description as string) ?? '',
            boothNumber: (data.boothNumber as string) ?? '',
            speakingTopics: (data.speakingTopics as string) ?? '',
            riderSoldOut: false,
          })
        }
      } catch {
        // scheduleEvents collection may not exist yet
      }

      merged.sort((a, b) => a.date.getTime() - b.date.getTime())
      setEvents(merged)
      setLoading(false)
    }

    fetchAll()
  }, [])

  async function handleLectureCheckout(eventId: string) {
    setCheckoutLoading(eventId)
    try {
      const result = await createLectureCheckout({ eventId })
      if (result.data.url) {
        window.location.href = result.data.url
      }
    } catch {
      alert('Something went wrong. Please try again or email info@advantagehorsemanship.com.')
    } finally {
      setCheckoutLoading(null)
    }
  }

  // Auto-hide events whose effective end date has passed by more than 24 hours.
  // Effective end = endDate when present, else date (the start). Defensive:
  // missing or invalid end defaults to showing the event.
  const visibleEvents = events.filter((e) => {
    const end = e.endDate ?? e.date
    if (!end || isNaN(end.getTime())) return true
    return end.getTime() + 24 * 60 * 60 * 1000 >= Date.now()
  })

  const filtered = filter === 'all' ? visibleEvents : visibleEvents.filter((e) => e.type === filter)

  const FILTERS: { key: FilterType; label: string }[] = [
    { key: 'all', label: 'All Events' },
    { key: 'masterclass', label: 'Masterclasses' },
    { key: 'lecture', label: 'Lectures' },
    { key: 'expo', label: 'Expos' },
  ]

  return (
    <Layout>
      <ManagedSeo path="/schedule" />

      {/* ─── HERO ──────────────────────────────────────────────── */}
      <section className="bg-navy py-16 lg:py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto text-center">
          <p className="font-body text-gold text-sm font-semibold tracking-widest uppercase mb-4">
            Schedule
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white leading-tight mb-4">
            Events and Appearances
          </h1>
          <p className="font-body text-base text-white/60 max-w-2xl mx-auto">
            Masterclasses, lectures, and expo appearances. Find Scott near you.
          </p>
        </div>
      </section>

      {/* ─── FILTER BAR ────────────────────────────────────────── */}
      <section className="bg-white border-b border-navy/10 py-4 px-4 sm:px-6 lg:px-8 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`font-body text-sm font-medium px-5 py-2.5 min-h-[44px] transition-colors ${
                filter === f.key
                  ? 'bg-gold text-navy'
                  : 'bg-navy/5 text-navy/60 hover:bg-navy/10'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </section>

      {/* ─── EVENT LIST ────────────────────────────────────────── */}
      <section className="py-12 lg:py-16 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#F5F5F5' }}>
        <div className="max-w-5xl mx-auto">
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16">
              <p className="font-body text-base text-navy/50 mb-4">
                No upcoming events scheduled. Check back soon.
              </p>
              <Link
                to="/contact"
                className="font-body text-sm font-medium text-gold hover:opacity-80 transition-opacity"
              >
                Inquire about booking Scott
              </Link>
            </div>
          ) : (
            <div className="space-y-6">
              {filtered.map((event) => {
                const durationDays = event.durationDays ?? 3
                const dayLabels = event.type === 'masterclass'
                  ? getEventDayLabels(event.date, durationDays)
                  : []
                const riderPrice = durationDays === 2 ? '$599' : '$849'
                const riderDescriptor = `Full ${durationDays}-day participation with your horse · ${riderPrice}`
                const auditorGridCols = durationDays === 2 ? 'grid-cols-2' : 'grid-cols-3'
                return (
                <div key={event.id} className="bg-white p-5 sm:p-7 border border-navy/10">
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                    {/* LEFT COLUMN */}
                    <div className="flex-1 min-w-0">
                      {/* Badge */}
                      {event.type === 'masterclass' && (
                        <span className="inline-flex items-center gap-2 flex-wrap">
                          <span className="inline-block bg-gold text-navy text-xs uppercase tracking-widest px-3 py-1 font-semibold">
                            Masterclass
                          </span>
                          <span className="inline-block font-body text-[10px] font-bold tracking-wider uppercase px-2 py-1 border border-navy/30 text-navy/70">
                            {durationDays}-Day
                          </span>
                        </span>
                      )}
                      {event.type === 'lecture' && (
                        <span className="inline-block bg-navy text-white text-xs uppercase tracking-widest px-3 py-1 font-semibold">
                          Lecture
                        </span>
                      )}
                      {event.type === 'expo' && (
                        <span className="inline-block border border-gold text-gold text-xs uppercase tracking-widest px-3 py-1 font-semibold">
                          Expo
                        </span>
                      )}

                      <h3 className="font-heading text-2xl text-navy mt-3">
                        {event.title}
                      </h3>
                      <p className="font-body text-sm text-navy/80 mb-1">
                        {event.location}
                        {event.venueName ? ` · ${event.venueName}` : ''}
                      </p>
                      {event.venueAddress && (
                        <p className="font-body text-sm text-navy/60 mb-1">
                          {event.venueAddress}
                        </p>
                      )}
                      <p className="font-body text-sm font-semibold text-gold">
                        {formatDateRange(event.date, event.endDate)}
                      </p>
                      {event.type === 'masterclass' && (
                        <p className="font-body text-sm text-navy/70 mt-1">
                          Approximately 9:30am to 5:00pm daily.
                        </p>
                      )}
                      {event.type === 'expo' && event.boothNumber && (
                        <p className="font-body text-sm text-gold font-semibold mt-1">
                          Booth {event.boothNumber}
                        </p>
                      )}

                      {/* Expandable description — lecture and expo only */}
                      {(event.type === 'lecture' || event.type === 'expo') &&
                        (event.description || event.speakingTopics) && (
                          <div className="mt-3">
                            {expandedId === event.id ? (
                              <>
                                <p className="font-body text-sm text-navy/70 leading-relaxed">
                                  {event.description || event.speakingTopics}
                                </p>
                                <button
                                  onClick={() => setExpandedId(null)}
                                  className="font-body text-xs text-gold font-medium mt-1"
                                >
                                  Show less
                                </button>
                              </>
                            ) : (
                              <button
                                onClick={() => setExpandedId(event.id)}
                                className="font-body text-xs text-gold font-medium"
                              >
                                Read more
                              </button>
                            )}
                          </div>
                        )}
                    </div>

                    {/* RIGHT COLUMN — varies by type */}
                    {event.type === 'masterclass' && (
                      <div className="lg:w-72 flex flex-col">
                        {/* RIDER SECTION */}
                        <div>
                          <h4 className="text-sm tracking-widest uppercase text-navy font-semibold mb-2">
                            Rider Registration
                          </h4>
                          <p className="text-sm text-navy/70 mb-3">
                            {riderDescriptor}
                          </p>
                          {event.riderSoldOut ? (
                            <button
                              disabled
                              className="w-full font-body text-sm font-semibold border-2 border-gold/50 text-navy/40 px-6 py-2 min-h-[44px] cursor-not-allowed"
                            >
                              Sold Out
                            </button>
                          ) : (
                            <button
                              onClick={() => openTicketModal(event, 'rider')}
                              style={{
                                width: '100%',
                                backgroundColor: '#C8A84B',
                                color: '#0A1628',
                                fontWeight: '700',
                                padding: '10px 28px',
                                fontSize: '14px',
                                border: 'none',
                                cursor: 'pointer',
                                minHeight: '44px',
                                letterSpacing: '0.05em',
                                textTransform: 'uppercase',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              Get Rider Ticket
                            </button>
                          )}
                        </div>

                        {/* DIVIDER */}
                        <div className="border-t border-gold/40 my-5"></div>

                        {/* AUDITOR SECTION */}
                        <div>
                          <h4 className="text-sm tracking-widest uppercase text-navy font-semibold mb-2">
                            Auditor Day Passes
                          </h4>
                          <p className="text-sm text-navy/70 mb-3">
                            Observe Scott teaching live. No horse needed.
                          </p>
                          <div className={`grid ${auditorGridCols} gap-2`}>
                            {dayLabels.map((day) => (
                              <button
                                key={day}
                                onClick={() => openTicketModal(event, 'auditor', day)}
                                className="font-body text-xs font-semibold border border-navy/20 text-navy px-2 py-2 min-h-[40px] hover:bg-navy hover:text-white transition-colors"
                              >
                                {day} $20
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {event.type === 'lecture' && (
                      <div className="lg:w-80 flex flex-col gap-2 lg:items-end">
                        <p className="font-body text-lg font-semibold text-navy">
                          $25
                          <span className="text-xs text-navy/40 font-normal ml-1">per person</span>
                        </p>
                        <button
                          onClick={() => handleLectureCheckout(event.id)}
                          disabled={checkoutLoading === event.id}
                          className="inline-flex items-center justify-center font-body text-sm font-bold tracking-wider uppercase bg-gold text-navy px-6 min-h-[52px] hover:opacity-90 disabled:opacity-50"
                        >
                          {checkoutLoading === event.id ? 'Loading...' : 'Get Tickets'}
                        </button>
                      </div>
                    )}

                    {event.type === 'expo' && (
                      <div className="lg:w-80 flex flex-col gap-2 lg:items-end">
                        <button
                          onClick={() => generateIcs(event)}
                          className="inline-flex items-center gap-2 font-body text-sm font-bold tracking-wider uppercase border-2 border-navy text-navy px-6 min-h-[52px] hover:bg-navy hover:text-white transition-colors"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          Add to Calendar
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                )
              })}
            </div>
          )}
        </div>
      </section>
      <BringScottSection />
      {modalEvent && (
        <MasterclassTicketModal
          isOpen={modalEvent !== null}
          onClose={closeTicketModal}
          eventId={modalEvent.id.slice(3)}
          eventName={modalEvent.title}
          eventStartDate={modalEvent.date}
          ticketType={modalTicketType}
          day={modalDay}
          durationDays={modalEvent.durationDays ?? 3}
          riderSeatsRemaining={modalEvent.riderSeatsRemaining}
        />
      )}
    </Layout>
  )
}

// ─── BRING SCOTT TO YOU SECTION ────────────────────────────────────────────

type Offering = {
  color: string
  icon: React.ReactNode
  title: string
  description: string
}

const OFFERINGS: Offering[] = [
  {
    color: '#C8A84B',
    icon: (
      <svg
        width="28"
        height="28"
        viewBox="0 0 28 28"
        fill="none"
        stroke="#C8A84B"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        {/* Microphone head */}
        <rect x="10" y="3" width="8" height="13" rx="4" />
        {/* Arc base */}
        <path d="M6 13c0 4.4 3.6 8 8 8s8-3.6 8-8" />
        {/* Stand */}
        <line x1="14" y1="21" x2="14" y2="25" />
        <line x1="10" y1="25" x2="18" y2="25" />
      </svg>
    ),
    title: 'The Real Secret Lecture',
    description:
      'A standalone lecture event at a venue near you. Scott delivers his core methodology on why horses do what they do and how to fix it. Ideal for equestrian groups, expos, and barn events.',
  },
  {
    color: '#7EB8E0',
    icon: (
      <svg
        width="28"
        height="28"
        viewBox="0 0 28 28"
        fill="none"
        stroke="#7EB8E0"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        {/* Three people heads */}
        <circle cx="5" cy="8" r="2" />
        <circle cx="11" cy="8" r="2" />
        <circle cx="17" cy="8" r="2" />
        {/* Bodies */}
        <path d="M2 18c0-2.2 1.3-4 3-4s3 1.8 3 4" />
        <path d="M8 18c0-2.2 1.3-4 3-4s3 1.8 3 4" />
        <path d="M14 18c0-2.2 1.3-4 3-4s3 1.8 3 4" />
        {/* Horse */}
        <path d="M21 22c1.5-3 4-4.5 5-4.5s1.5 1 1.5 2" />
        <path d="M22 22l0 3M25 22l0 3" />
        <path d="M21 22c-.5-1.5 0-3 1-4s1.5-1 2.5-1" />
        <path d="M25 17l-1.5-.5" />
      </svg>
    ),
    title: 'Riders Foundation Masterclass',
    description:
      'A three-day immersive hands-on experience. Scott works directly with up to 10 riders and their horses. Auditors welcome. This is the most transformative in-person experience Scott offers.',
  },
  {
    color: '#5DCAA5',
    icon: (
      <svg
        width="28"
        height="28"
        viewBox="0 0 28 28"
        fill="none"
        stroke="#5DCAA5"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        {/* Person */}
        <circle cx="5" cy="7" r="2" />
        <path d="M5 9v5" />
        <path d="M3 11l2-.5 2 .5" />
        <path d="M5 14l-1.2 5M5 14l1.2 5" />
        {/* Horse */}
        <path d="M12 20c1.5-3.5 5-5.5 8-6.5s3.5 1.5 3.5 3" />
        <path d="M14 20l-1 4M17 20l0 4M22 19l1 5M24 17l2.5 3.5" />
        <path d="M12 20c-1-2.5-.5-5 1.5-7s3-2 5-2" />
        <path d="M18.5 11l-2.5-.8" />
      </svg>
    ),
    title: 'Riding Intensive',
    description:
      'A focused two-hour private session with Scott. Available locally in Fredericksburg VA or as a travel booking. For riders who want direct one-on-one work.',
  },
]

const BRING_SCOTT_STYLES = `
  .bs-card {
    background-color: rgba(255,255,255,0.03);
    border: 1px solid rgba(200,168,75,0.15);
    transition: border-color 0.22s ease, transform 0.22s ease;
  }
  .bs-card:hover {
    border-color: rgba(200,168,75,0.4);
    transform: translateY(-3px);
  }
  .bs-cta:hover {
    opacity: 0.92;
  }
`

function BringScottSection() {
  const [modalOpen, setModalOpen] = useState(false)
  return (
    <section
      style={{
        backgroundColor: '#0A1628',
        borderTop: '1px solid rgba(200,168,75,0.2)',
        padding: '80px 24px',
      }}
    >
      <style>{BRING_SCOTT_STYLES}</style>
      <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
        <div className="text-center" style={{ marginBottom: '48px' }}>
          <p
            className="font-body font-semibold uppercase"
            style={{
              fontSize: '11px',
              letterSpacing: '3px',
              color: '#C8A84B',
              marginBottom: '16px',
            }}
          >
            Bring Scott to You
          </p>
          <h2
            className="font-heading font-semibold leading-tight"
            style={{
              fontSize: '40px',
              color: '#FFFFFF',
              marginBottom: '16px',
            }}
          >
            Do not see a date near you?
          </h2>
          <p
            className="font-body mx-auto"
            style={{
              fontSize: '15px',
              color: 'rgba(255,255,255,0.55)',
              maxWidth: '620px',
              lineHeight: 1.65,
            }}
          >
            Scott travels for select events. Whether you are a barn owner, a
            trainer, an expo organizer, or a group of riders looking for an
            experience, here is what he offers in person.
          </p>
        </div>

        <div
          className="grid grid-cols-1 md:grid-cols-3"
          style={{ gap: '20px' }}
        >
          {OFFERINGS.map((o) => (
            <OfferingCard key={o.title} {...o} />
          ))}
        </div>

        <div style={{ textAlign: 'center', marginTop: '48px' }}>
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            className="bs-cta font-body"
            style={{
              backgroundColor: '#C8A84B',
              color: '#0A1628',
              fontWeight: 600,
              padding: '16px 40px',
              borderRadius: '6px',
              border: 'none',
              cursor: 'pointer',
              fontSize: '14px',
              transition: 'opacity 0.15s ease',
            }}
          >
            Inquire About Hosting Scott
          </button>
          <p
            className="font-body"
            style={{
              fontSize: '12px',
              color: 'rgba(255,255,255,0.4)',
              marginTop: '14px',
            }}
          >
            Scott personally reviews all inquiries and responds within 48 hours.
          </p>
        </div>
      </div>
      <BringScottModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </section>
  )
}

function OfferingCard({ color, icon, title, description }: Offering) {
  return (
    <div
      className="bs-card relative"
      style={{
        borderRadius: '12px',
        padding: '28px 24px',
        paddingTop: '31px',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '3px',
          backgroundColor: color,
          borderTopLeftRadius: '12px',
          borderTopRightRadius: '12px',
        }}
      />
      <div style={{ marginBottom: '16px' }}>{icon}</div>
      <h3
        className="font-heading font-semibold text-white leading-tight"
        style={{ fontSize: '20px', marginBottom: '10px' }}
      >
        {title}
      </h3>
      <p
        className="font-body"
        style={{
          fontSize: '14px',
          color: 'rgba(255,255,255,0.65)',
          lineHeight: 1.6,
        }}
      >
        {description}
      </p>
    </div>
  )
}
